import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import RiskScoreResult from '../../components/RiskScoreResult';
import CalcShell from '../../components/CalcShell';
import NumberField from '../../components/NumberField';
import { eltsRiskGroups, ELTS_CALCULATOR_URL } from '../../data/diseasesData';
import { RiskLevel } from '../../types';
import { useUserInput } from '../../context/UserInputContext';

const calculateElts = (
  age: number,
  spleenSize: number,
  blasts: number,
  plateletCount: number
): number =>
  0.0025 * Math.pow(age / 10, 3) +
  0.0615 * spleenSize +
  0.1052 * blasts +
  0.4104 * Math.pow(plateletCount / 1000, -0.5);

/**
 * The official EUTOS calculator rounds the score to four decimal places and
 * then applies the cut-offs, so a raw 1.56804 is a *low* risk 1.5680 rather
 * than an intermediate one. Classify the rounded value, not the raw one.
 */
const getEltsRiskLevel = (roundedScore: number): RiskLevel => {
  if (roundedScore <= 1.568) return 'low';
  if (roundedScore <= 2.2185) return 'intermediate';
  return 'high';
};

const EltsCalculator: React.FC = () => {
  const { t } = useTranslation('hematology');
  const { state, update } = useUserInput();
  const input = state.elts;
  const setInput = (val: typeof input) => update('elts', val);

  const result = useMemo(() => {
    const age = parseFloat(input.age);
    const spleen = parseFloat(input.spleenSize);
    const blasts = parseFloat(input.blasts);
    const platelets = parseFloat(input.plateletCount);

    if (
      isNaN(age) ||
      isNaN(spleen) ||
      isNaN(blasts) ||
      isNaN(platelets) ||
      platelets <= 0
    ) {
      return null;
    }

    const raw = calculateElts(age, spleen, blasts, platelets);
    const rounded = Number(raw.toFixed(4));

    return { score: rounded.toFixed(4), level: getEltsRiskLevel(rounded) };
  }, [input]);

  const inputs = (
    <>
      <NumberField
        label={t('elts.ageLabel')}
        value={input.age}
        onChange={(v) => setInput({ ...input, age: v })}
        placeholder={t('elts.agePlaceholder')}
        min={0}
        max={120}
      />

      <NumberField
        label={t('elts.spleenLabel')}
        value={input.spleenSize}
        onChange={(v) => setInput({ ...input, spleenSize: v })}
        placeholder={t('elts.spleenPlaceholder')}
        hint={t('elts.spleenHint')}
        min={0}
        step={0.1}
        decimal
      />

      <NumberField
        label={t('elts.blastsLabel')}
        value={input.blasts}
        onChange={(v) => setInput({ ...input, blasts: v })}
        placeholder={t('elts.blastsPlaceholder')}
        min={0}
        max={100}
        step={0.1}
        decimal
      />

      <NumberField
        label={t('elts.plateletsLabel')}
        value={input.plateletCount}
        onChange={(v) => setInput({ ...input, plateletCount: v })}
        placeholder={t('elts.plateletsPlaceholder')}
        min={1}
      />
    </>
  );

  const info = (
    <>
      <p className="formula" dir="ltr">
        ELTS = 0.0025 × (age/10)³ + 0.0615 × spleen + 0.1052 × blasts + 0.4104 ×
        (platelets/1000)⁻⁰·⁵
      </p>
      <p className="calc-shell__info-note">
        {t('elts.roundingNote')}{' '}
        <a href={ELTS_CALCULATOR_URL} target="_blank" rel="noopener noreferrer">
          {t('elts.officialCalc')}
        </a>
      </p>
    </>
  );

  return (
    <CalcShell
      title="ELTS — EUTOS Long-Term Survival Score"
      subtitle={t('elts.subtitle')}
      info={info}
      inputs={inputs}
      aside={
        result && (
          <div className="score-display">
            <span className="score-label">ELTS Score</span>
            <span className="score-value">{result.score}</span>
          </div>
        )
      }
    >
      <RiskScoreResult
        groups={eltsRiskGroups}
        activeLevel={result?.level || null}
        emptyHint={t('elts.emptyHint')}
      />
    </CalcShell>
  );
};

export default EltsCalculator;
